import { useEffect, useRef, useState } from "react";
import usePlacesAutocomplete, { getDetails } from "use-places-autocomplete";
import { TextField } from "@radix-ui/themes";

export type LocationData = {
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zip?: string | null;
};

type LocationAutocompleteProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onLocationSelect?: (location: LocationData) => void;
  placeholder?: string;
};

export function LocationAutocomplete({
  id,
  value,
  onChange,
  onLocationSelect,
  placeholder = "Where are you based?",
}: LocationAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    init,
    ready,
    value: autocompleteValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({ initOnMount: false });

  // Load Google Maps API
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn(
        "VITE_GOOGLE_MAPS_API_KEY is not set — location autocomplete disabled",
      );
      setLoadError(true);
      return;
    }

    const loadGoogleMaps = async () => {
      try {
        const loadApi = (await import("load-google-maps-api")).default;
        await loadApi({ libraries: ["places"], key: apiKey });
        init();
      } catch (error) {
        console.warn("Failed to load Google Maps API:", error);
        setLoadError(true);
      }
    };
    loadGoogleMaps();
  }, [init]);

  // Sync external value with autocomplete
  useEffect(() => {
    if (value !== autocompleteValue) {
      setValue(value, false);
    }
  }, [value, autocompleteValue, setValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onChange(newValue);
    setIsOpen(true);
  };

  const handleSuggestionClick = async (
    suggestion: google.maps.places.AutocompletePrediction,
  ) => {
    try {
      const res = (await getDetails({
        placeId: suggestion.place_id,
        language: "en",
        fields: ["address_components"],
      })) as google.maps.places.PlaceResult;

      if (res?.address_components) {
        const components = res.address_components;

        const city =
          components.find((c) => c.types.includes("locality"))?.long_name ||
          components.find((c) => c.types.includes("sublocality"))?.long_name ||
          components.find((c) =>
            c.types.includes("administrative_area_level_2"),
          )?.long_name;

        const state = components.find((c) =>
          c.types.includes("administrative_area_level_1"),
        )?.long_name;

        const countryComponent = components.find((c) =>
          c.types.includes("country"),
        );
        const country =
          countryComponent?.long_name === "United States"
            ? countryComponent.short_name
            : countryComponent?.long_name;

        const zip = components.find((c) =>
          c.types.includes("postal_code"),
        )?.long_name;

        let formattedAddress = [city, country].filter(Boolean).join(", ");
        if (country === "US") {
          formattedAddress = [city, state].filter(Boolean).join(", ");
        }

        setValue(formattedAddress, false);
        onChange(formattedAddress);
        onLocationSelect?.({ city, state, country, zip });
      }

      clearSuggestions();
      setIsOpen(false);
    } catch (error) {
      console.error("Error selecting location:", error);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <TextField.Root
        id={id}
        type="text"
        value={autocompleteValue}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        disabled={!ready && !loadError}
        placeholder={placeholder}
      />

      {isOpen && status === "OK" && data.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {data.map((suggestion) => (
            <button
              key={suggestion.place_id}
              type="button"
              className="w-full text-left px-4 py-3 hover:bg-gray-100 border-b border-gray-100 last:border-b-0 text-sm"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion.description}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
